import type {
  AudioLanguage,
  CastMember,
  ContentRating,
  Episode,
  Show,
  ShowStatus,
} from '@gobingebox/shared';
import { apiFetch } from '@/lib/api';

export interface ShowInput {
  title?: string;
  description?: string;
  status?: ShowStatus;
  rating?: ContentRating | null;
  genreIds?: string[];
  cast?: CastMember[];
  previewStreamUid?: string | null;
}

export interface EpisodeInput {
  season?: number;
  number?: number;
  title?: string;
  description?: string;
  releaseAt?: string | null;
  audioLanguages?: AudioLanguage[];
  durationSeconds?: number | null;
  streamUid?: string | null;
}

const json = (method: string, data: unknown): RequestInit => ({ method, body: JSON.stringify(data) });

export const contentApi = {
  shows: () => apiFetch<{ shows: Show[] }>('/content/shows'),
  show: (slug: string) => apiFetch<{ show: Show; episodes: Episode[] }>(`/content/shows/${slug}`),
  createShow: (input: ShowInput) => apiFetch<{ show: Show }>('/content/shows', json('POST', input)),
  updateShow: (id: string, input: ShowInput) =>
    apiFetch<{ ok: boolean }>(`/content/shows/${id}`, json('PATCH', input)),
  deleteShow: (id: string) => apiFetch<{ ok: boolean }>(`/content/shows/${id}`, { method: 'DELETE' }),
  addEpisode: (showId: string, input: EpisodeInput) =>
    apiFetch<{ episode: Episode }>(`/content/shows/${showId}/episodes`, json('POST', input)),
  updateEpisode: (id: string, input: EpisodeInput) =>
    apiFetch<{ ok: boolean }>(`/content/episodes/${id}`, json('PATCH', input)),
  deleteEpisode: (id: string) =>
    apiFetch<{ ok: boolean }>(`/content/episodes/${id}`, { method: 'DELETE' }),
  uploadUrl: () =>
    apiFetch<{ uploadURL: string; uid: string }>('/content/stream/upload-url', json('POST', {})),
};

/** Upload a video file to a Cloudflare Stream one-time URL; returns the video uid. */
export async function uploadVideo(file: File): Promise<string> {
  const { uploadURL, uid } = await contentApi.uploadUrl();
  const body = new FormData();
  body.append('file', file);
  const res = await fetch(uploadURL, { method: 'POST', body });
  if (!res.ok) throw new Error('upload_failed');
  return uid;
}
